import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {Word} from '../../dtos/vocab/entry/word';
import {WordService} from '../../services/vocab/word.service';
import {CategoryService} from '../../services/vocab/category.service';
import {HttpParams} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {NewWordComponent} from './create/new-word/new-word.component';
import {FormOptionsLists} from '../../shared/FormOptionsLists';
import {AuthService} from '../../services/auth/auth.service';
import {NotificationService} from '../../services/common/notification.service';
import {BreakpointObserver, Breakpoints, BreakpointState} from '@angular/cdk/layout';
import {NewCategoryComponent} from "./create/new-category/new-category.component";
import {CategoryTreeComponent, EventData} from "../common/category-tree/category-tree.component";
import {TableComponent} from "../common/table/table.component";
import {SearchTriggerObject} from "../../shared/SearchTrigger";
import {filter} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {SharedService} from "../../services/common/shared.service";

@Component({
  selector: 'app-vocabulary',
  templateUrl: './vocabulary.component.html',
  styleUrls: ['./vocabulary.component.scss'],
  standalone: false
})

export class VocabularyComponent implements OnInit, OnDestroy {

  @ViewChild(TableComponent) tableC: TableComponent;
  @ViewChild(CategoryTreeComponent) treeC: CategoryTreeComponent;

  // table data and loading status
  displayedColumns: string[];
  data: Word[] = [];
  resultsLength = 25;
  details: [string, any][];


  // filter options
  formOptionsLists = new FormOptionsLists();
  // filter by level type
  levelFilterModeList = this.formOptionsLists.levelFilterModeList;
  levelFilterModeControl = new UntypedFormControl(this.levelFilterModeList[0]);
  // filter by category
  categoryControl: number = null;

  // error handling
  error = false;

  isAuthorized: boolean;
  isAdmin: boolean;
  displayEditingTools = false;

  // checkboxes to add entries to a Sammelmappe, off until the user asks for them
  selectMode = false;

  toggleSelectMode(): void {
    this.selectMode = !this.selectMode;
  }

  // child component
  reloadRootsTrigger = false;
  searchTrigger: SearchTriggerObject = {trigger: false, params: new HttpParams()}
  resetCategoryControl = false;
  resetSearchTrigger = false;

  // for references
  entryId: number = null;
  categoryId: number = null;
  initialSearchQuery: string = null;

  constructor(protected wordService: WordService, protected categoryService: CategoryService,
              private notificationService: NotificationService, public authService: AuthService,
              public matDialog: MatDialog, private observer: BreakpointObserver, private route: ActivatedRoute,
              private sharedService: SharedService, private router: Router) {
  }


  ngOnInit() {
    [this.isAuthorized, this.isAdmin] = this.authService.isAuthorizedAndAdmin()
    this.reloadRootsTrigger = !this.reloadRootsTrigger;
    if (this.displayEditingTools === true && this.isAdmin) {
      this.displayedColumns = this.formOptionsLists.displayedColumnsVocabEditing;
    } else {
      this.displayedColumns = this.formOptionsLists.displayedColumnsVocabDetails;
    }
    this.observer.observe([
      '(max-width:529px)',
      '(min-width:530px) and (max-width:899px)',
      '(min-width:900px) and (max-width:1089px)',
      '(min-width:1090px)',
      Breakpoints.Small,
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge
    ]).subscribe((breakpointState: BreakpointState) => {
      if (breakpointState.breakpoints['(max-width:529px)']) {
        if (this.displayEditingTools === true && this.isAdmin) {
          this.displayedColumns = this.formOptionsLists.displayedColumnsVocabEditing.filter(s => s !== 'country' && s !== 'category');
        } else {
          this.displayedColumns = this.formOptionsLists.displayedColumnsVocabDetails.filter(s => s !== 'country' && s !== 'category');
        }
      }
      if (breakpointState.breakpoints['(min-width:530px) and (max-width:899px)']) {
        if (this.displayEditingTools === true && this.isAdmin) {
          this.displayedColumns = this.setDisplayedColumnsWithLangLevelType(this.formOptionsLists.displayedColumnsVocabEditing.filter(s => s !== 'country'));
        } else {
          this.displayedColumns = this.setDisplayedColumnsWithLangLevelType(this.formOptionsLists.displayedColumnsVocabDetails.filter(s => s !== 'country'));
        }
      }
      if (breakpointState.breakpoints['(min-width:1090px)']) {
        if (this.displayEditingTools === true && this.isAdmin) {
          this.displayedColumns = this.setDisplayedColumnsWithLangLevelType(this.formOptionsLists.displayedColumnsVocabEditing);
        } else {
          this.displayedColumns = this.setDisplayedColumnsWithLangLevelType(this.formOptionsLists.displayedColumnsVocabDetails);
        }
      }
    });
    this.route.queryParams
      .pipe(filter(params => !!params['id'] || !!params['c_id'] || !!params['q']))
      .subscribe((params: HttpParams) => {
        if (params['q']) {
          this.initialSearchQuery = params['q'];
          // clear any previously selected category
          this.resetCategoryControl = !this.resetCategoryControl;
          this.categoryControl = null;
        }
        this.entryId = params['id'];
        if (this.entryId) {
          this.resetCategoryControl = !this.resetCategoryControl;
          this.categoryControl = null;
          let httpParams = new HttpParams();
          httpParams = httpParams.set('id', this.entryId);
          this.handleSearch(httpParams)
        }
        // category tree is all handled in the tree component
        this.categoryId = params['c_id'];
        if (this.categoryId) {
          // clear the rest of the filter so it doesn't combine with the deep-linked category
          this.resetSearchTrigger = !this.resetSearchTrigger;
          if (this.tableC) {
            this.tableC.sort.active = 'created';
            this.tableC.paginator.pageSize = 25;
            this.tableC.paginator.pageIndex = 0;
          }
        }
        this.sharedService.clearIdParam(this.router);
      });
  }

  /**
   * toggles displayEditingTools after clicking on "Bearbeiten" or "Ansicht"
   *
   * @param change boolean to decide if it should toggle the view
   */
  toggleEditingTools(change: boolean) {
    if (change) {
      this.displayEditingTools = !this.displayEditingTools;
    }
    if (this.displayEditingTools && this.isAdmin) {
      if (!this.displayedColumns.includes('edit')) {
        const lastIndex = this.displayedColumns.length - 1;
        this.displayedColumns.splice(lastIndex, 0, 'edit');
      }
    } else {
      this.displayedColumns = this.displayedColumns.filter(c => c !== 'edit');
    }

  }

  /**
   * opens a popup to create a new category, word- or DACH- field
   */
  openNewCategoryDialog() {
    const dialogRef = this.matDialog.open(NewCategoryComponent);
    dialogRef.afterClosed().subscribe({
      next: () => {
        this.reloadRootsTrigger = !this.reloadRootsTrigger;
      },
      error: (error) => {
        this.notificationService.openSnackBar(error);
      }
    });
  }

  /**
   * opens a popup to create a new word
   */
  openNewWordDialog() {
    const dialogRef = this.matDialog.open(NewWordComponent);
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          this.searchTrigger.params = new HttpParams();
        }
      },
      error: (error) => {
        this.notificationService.openSnackBar(error);
      }
    });
  }

  ngOnDestroy(): void {
    this.observer.ngOnDestroy();
  }

  setDisplayedColumnsWithLangLevelType(columns) {
    switch (this.levelFilterModeControl.value) {
      case this.levelFilterModeList[0]:
        if (this.displayEditingTools === true) {
          return columns.filter(s => s !== 'receptive');
        } else {
          return columns.filter(s => s !== 'receptive');
        }
      case this.levelFilterModeList[1]:
        if (this.displayEditingTools === true) {
          return columns.filter(s => s !== 'productive');
        } else {
          return columns.filter(s => s !== 'productive');
        }
      default:
        return columns;
    }
  }

  sendExportVocabularyRequest() {
    this.wordService.exportAll().subscribe(x => {
      // It is necessary to create a new blob object with mime-type explicitly set
      // otherwise only Chrome works like it should
      const newBlob = new Blob([x.body], {type: 'text/csv'});
      const data = window.URL.createObjectURL(newBlob);

      const link = document.createElement('a');
      link.href = data;
      link.download = x.headers.get('content-disposition').split('filename=')[1];
      // this is necessary as link.click() does not work on the latest firefox
      link.dispatchEvent(new MouseEvent('click', {bubbles: true, cancelable: true, view: window}));

      setTimeout(function () {
        // For Firefox, it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(data);
        link.remove();
      }, 100);
    });
  }

  /* -------------------- Category Tree -------------------- */

  handleChildSearch(data: EventData) {
    this.categoryControl = data.categoryControl;
    if (data.resetPageIndex && this.tableC) {
      this.tableC.paginator.pageIndex = 0;
    }
    this.entryId = null;
    this.resetSearchTrigger = !this.resetSearchTrigger;
    this.searchTrigger.trigger = !this.searchTrigger.trigger;
  }

  /* -------------------- Search Bar  -------------------- */

  /**
   * search for words with all of selected filters
   */
  handleSearch(params: HttpParams) {
    this.searchTrigger.params = params
  }

  handleResetFilter() {
    this.tableC.sort.active = 'created';
    this.tableC.paginator.pageSize = 25;
    this.tableC.paginator.pageIndex = 0;
    this.resetCategoryControl = !this.resetCategoryControl
    this.categoryControl = null
  }
}
